import { CasePermission, FieldType, FcaResponseResult, Address, BankAccountResponse, FirmFcaData, LenderFirmDraftDto, FirmGetById, FirmGetAllResponse, DraftFirmOperation, FirmType, OperationType, DraftTradingAddress } from '@pmp/api';
import { TFunction } from 'i18next';
import { NotificationInstance } from 'antd/es/notification/interface';
import type { FormInstance } from 'antd';
import type { MutableRefObject, Dispatch, SetStateAction } from 'react';
import type { BDMValue } from './components/BDMField';
import styles from './styles.module.css';

export const FIRMS_ENTITY_LIST_NAMES: FieldType[] = [
  FieldType.EXCLUSIVITY_SETS,
  FieldType.FIRM_TYPE,
  FieldType.YES_NO,
  FieldType.COMPLIANCE_STATUS,
  FieldType.OPERATION_TYPE,
  FieldType.CASE_PERMISSION_TYPE,
  FieldType.FIRMS,
  FieldType.PRINCIPALS,
  FieldType.PACKAGERS,
  FieldType.PAYMENT_ROUTE_FIRMS,
  FieldType.PROCURATION_FEE_UNIT,
];

export { OPERATION_ORDER } from '@pmp/utils';

// CSV import references firms by OhpenId (not FirmId), so it needs the same
// role-filtered lists as the manual CRUD form above (PRINCIPALS/PACKAGERS/
// PAYMENT_ROUTE_FIRMS), keyed by OhpenId instead of FirmId.
export const FIRMS_IMPORT_VALUE_LIST_NAMES: FieldType[] = [
  FieldType.FIRM_TYPE,
  FieldType.CASE_PERMISSION_TYPE,
  FieldType.PRINCIPALS_OHPEN_ID,
  FieldType.PACKAGERS_OHPEN_ID,
  FieldType.PAYMENT_ROUTE_FIRMS_OHPEN_ID,
  FieldType.YES_NO,
  FieldType.OPERATION_TYPE,
  FieldType.REGIONS,
  FieldType.BDMS,
  FieldType.PROCURATION_FEE_UNIT,
];

export const MANDATORY_BANK_OPERATIONS = new Set(['NETWORK', 'MORTGAGE_CLUB']);

export const FCA_RESULT_MESSAGES: Partial<Record<FcaResponseResult, string>> = {
  [FcaResponseResult.PARTIAL_DATA]: 'Some FCA data could not be loaded.',
  [FcaResponseResult.REFERENCE_NOT_FOUND]: 'The FRN is incorrect. No firm found.',
  [FcaResponseResult.FCA_ERROR]: 'FCA returned an error. Please try again later.',
};

export const showFcaNotification = (result: FcaResponseResult, t: TFunction, notification: NotificationInstance) => {
  switch (result) {
    case FcaResponseResult.OK:
      notification.success({ title: t('lender.firms.labels.ok') });
      break;
    case FcaResponseResult.PARTIAL_DATA:
      notification.warning({ title: t('lender.firms.labels.partialData') });
      break;
    case FcaResponseResult.REFERENCE_NOT_FOUND:
      notification.warning({ title: t('lender.firms.labels.referenceNotFound') });
      break;
    case FcaResponseResult.FCA_ERROR:
      notification.error({ title: t('lender.firms.labels.fcaError') });
      break;
  }
};

export const formatAddressAsLines = (addr: Address): string =>
  [addr?.lineItems?.filter(Boolean).join(' '), [addr?.city, addr?.postCode, addr?.country].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join('\n');

export const mapLenderFirmFormValuesToDraft = (values: FirmGetById, record: FirmGetAllResponse | null | undefined): LenderFirmDraftDto => {
  const { registrationDetails, contactDetails, payments, packaging, exclusives, status } = values;

  const operationsWhitelist: DraftFirmOperation[] = (registrationDetails?.operations ?? []).map(
    (op: OperationType) => {
      const bank = payments?.operations?.[op] as BankAccountResponse & { bankName?: string | null };
      const hasBank = bank?.accountName?.trim() || bank?.sortCode?.trim() || bank?.accountNumber?.trim() || bank?.bankName?.trim();
      return {
        type: op,
        bankAccountDetails: hasBank
          ? {
              accountName: bank?.accountName ?? null,
              accountNumber: bank?.accountNumber ?? null,
              sortCode: bank?.sortCode ?? null,
              bankName: bank?.bankName ?? null,
            }
          : null,
      };
    },
  );

  // The two packaging directions are mutually exclusive, and the form only renders one of them,
  // so drop whichever side is hidden instead of submitting whatever it held before.
  const isPackager = operationsWhitelist.some(op => op.type === OperationType.PACKAGE);

  const boostValue = values.procFeeBoost?.value;
  const boostUnit = values.procFeeBoost?.unit;

  return {
    id: values.firmId ?? record?.id ?? null,
    name: registrationDetails?.name ?? '',
    // Sent as null rather than defaulted, otherwise the server's "Firm type is required" rule can never fire.
    type: registrationDetails?.type ?? null,
    frn: registrationDetails?.type === FirmType.NOT_FCA_REGISTERED ? null : (registrationDetails?.frn != null ? Number(registrationDetails.frn) : null),
    principalFirmIds: registrationDetails?.principalIds ?? [],
    isActive: registrationDetails?.isActive ?? false,
    nacfbNumber: registrationDetails?.nacfbNumber ?? null,
    reference: registrationDetails?.reference ?? null,
    btl: registrationDetails?.btl ?? CasePermission.CREATE_AND_MANAGE,
    rbtl: registrationDetails?.rbtl ?? CasePermission.CREATE_AND_MANAGE,
    rmc: registrationDetails?.rmc ?? CasePermission.CREATE_AND_MANAGE,
    operationsWhitelist,
    defaultPaymentRouteFirmId: payments?.defaultPaymentRouteFirmId ?? null,
    paymentRouteFirmIds: payments?.paymentRouteFirmIds ?? null,
    packagerFirmIds: isPackager ? [] : (packaging?.packagerFirmIds ?? []),
    packagerForFirmIds: isPackager ? (packaging?.packagerForFirmIds ?? []) : [],
    procFeeBoost:
      boostValue !== null && boostValue !== undefined && boostUnit !== null && boostUnit !== undefined
        ? { value: boostValue, unit: boostUnit }
        : null,
    idsOfExclusivitySets: exclusives ?? [],
    complianceStatus: status?.complianceStatus ?? null,
    onTheWatchlist: status?.onTheWatchlist ?? null,
    qualifiedDomains: contactDetails?.qualifiedDomains ?? null,
    email: contactDetails?.email ?? null,
    mobilePhone: contactDetails?.mobilePhone ?? null,
    workPhone: contactDetails?.workPhone ?? null,
    website: contactDetails?.website ?? null,
    complaintsWebsite: contactDetails?.complaintsWebsite ?? null,
    firmAddress: contactDetails?.firmAddress ?? null,
    tradingAddresses: ((values.tradingNames ?? []) as Array<Address & { id?: string | null; bdm?: BDMValue | string | null; businessDevelopmentManager?: string | null; isActive?: boolean | null }>).map((tn): DraftTradingAddress => {
      const bdmRaw = tn.bdm;
      const bdmObj = typeof bdmRaw === 'object' && bdmRaw !== null ? bdmRaw : null;
      return {
        id: tn.id ?? null,
        name: tn.name ?? null,
        numberOrBuilding: tn.numberOrBuilding ?? null,
        postCode: tn.postCode?.toUpperCase() ?? '',
        city: tn.city ?? null,
        country: tn.country ?? null,
        lineItems: tn.lineItems ?? null,
        regionId: bdmObj?.regionId ?? null,
        bdmId: bdmObj?.bdmId ?? null,
        // Names carried for the change-request diff display; the BRS write uses the ids only.
        region: bdmObj?.regionName ?? null,
        businessDevelopmentManager: bdmObj?.bdmName ?? null,
        isActive: tn.isActive ?? null,
      };
    }),
    lockedByActionId: values.lockedByActionId ?? null,
  };
};

export const resetFieldsOnTypeChange = (form: FormInstance<FirmGetById> | undefined, value: string): void => {
  if (value !== FirmType.APPOINTED_REPRESENTATIVE) {
    form?.setFieldValue(['registrationDetails', 'principalIds'], []);
  }
  if (value !== FirmType.APPOINTED_REPRESENTATIVE && value !== FirmType.DIRECTLY_AUTHORISED) {
    form?.setFieldValue(['packaging', 'packagerFirmIds'], []);
  }
  if (value !== FirmType.DIRECTLY_AUTHORISED) {
    const ops: string[] = form?.getFieldValue(['registrationDetails', 'operations']) ?? [];
    form?.setFieldValue(['registrationDetails', 'operations'], ops.filter(o => o !== 'NETWORK'));
  }
  if (value === FirmType.DIRECTLY_AUTHORISED || value === FirmType.APPOINTED_REPRESENTATIVE) {
    const ops: string[] = form?.getFieldValue(['registrationDetails', 'operations']) ?? [];
    if (!ops.includes('ADVICE')) {
      form?.setFieldValue(['registrationDetails', 'operations'], [...ops, 'ADVICE']);
    }
  }
  if (value === FirmType.NOT_FCA_REGISTERED) {
    const ops: string[] = form?.getFieldValue(['registrationDetails', 'operations']) ?? [];
    form?.setFieldValue(['registrationDetails', 'operations'], ops.filter(o => o !== 'MORTGAGE_CLUB'));
  }
  if (value === FirmType.NOT_FCA_REGISTERED) {
    form?.setFieldValue(['registrationDetails', 'frn'], undefined);
  }
};

export const getHighlightProps = (dataIndex: string[], fcaModifiedFields: FirmFcaData | null | undefined) => {
  if (!fcaModifiedFields) return '';
  const tab = dataIndex[0] as keyof FirmFcaData;
  const field = dataIndex[1] as keyof FirmFcaData[typeof tab];

  const isChanged = fcaModifiedFields[tab]?.[field] !== undefined || (tab === 'tradingNames' && fcaModifiedFields[tab] !== undefined); // Highlight all trading name fields if any have changed, since we don't track individual field changes within trading names
  return isChanged ? styles.highlightedField : '';
};

export const cleanupRemovedOperations = (
  prevOperationsRef: MutableRefObject<string[]>,
  operations: string[],
  form: FormInstance<FirmGetById> | undefined,
  setBankDetailsVisible: Dispatch<SetStateAction<Set<string>>>,
): void => {
  const prevOps = prevOperationsRef.current;
  prevOperationsRef.current = operations;
  const removed = prevOps.filter(op => !operations.includes(op));
  if (removed.length === 0) return;
  removed.forEach(op => form?.setFieldValue(['payments', 'operations', op], undefined));
  setBankDetailsVisible(current => {
    const next = new Set(current);
    removed.forEach(op => next.delete(op));
    return next;
  });
};
